<template>
    <div class="public-profile-header__top-bar">
        <div class="public-profile-header__top-bar--left-side">
            <div class="public-profile-header__top-bar--left-side-avatar">
                <common-company-avatar
                    v-if="company"
                    :company="company"
                    :shouldLeadToProfile="true"
                    size="tiny-small"
                ></common-company-avatar>
                <common-user-avatar v-if="user" :user="user" :shouldLeadToProfile="true" size="large"></common-user-avatar>
            </div>

            <div class="public-profile-header__top-bar--left-side-data">
                <div class="public-profile-header__top-bar--left-side-heading">
                    <common-page-header :heading="title" size="medium-small"></common-page-header>
                    <div class="public-profile-header__top-bar--left-side-verify">
                        <common-verify-badge
                            v-if="isUserProfile"
                            class="public-profile-header__top-bar--left-side-copy"
                            :user="user"
                            :size="'medium'"
                        ></common-verify-badge>
                    </div>
                </div>
                <div class="public-profile-header__order-by">
                    <slot></slot>
                </div>
                <common-button
                    v-if="buttonLabel && !$auth.isSubdomainActive()"
                    type="primary"
                    size="micro"
                    :label="buttonLabel"
                    @click="emitEvent({ name: 'action-click' })"
                ></common-button>
                <div class="public-profile-header__top-bar--left-side-click">
                    <div>
                        <div
                            v-if="!isEmailShow && user?.email?.length"
                            class="public-profile-header__info"
                            @click="isEmailShow = true"
                        >
                            {{ $t('components.user.user_card.click_email') }}
                        </div>
                        <div v-if="isEmailShow">
                            <a class="public-profile-header__top-bar--left-side-link" :href="`tel:${user?.email}`">
                                {{ user?.email }}
                            </a>
                            <common-icon
                                class="public-profile-header__top-bar--left-side-copy"
                                name="copy"
                                size="small"
                                title="Copy"
                                @click="copyToClipboard(user?.email)"
                            ></common-icon>
                        </div>
                    </div>
                    <div>
                        <div
                            v-if="!isPhoneShow && user?.mobile?.length"
                            class="public-profile-header__info"
                            @click="isPhoneShow = true"
                        >
                            {{ $t('components.user.user_card.click_phone') }}
                        </div>
                        <div v-if="isPhoneShow">
                            <a class="public-profile-header__top-bar--left-side-link" :href="`tel:${user?.mobile}`">
                                {{ user?.mobile }}
                            </a>

                            <common-icon
                                class="public-profile-header__top-bar--left-side-copy"
                                name="copy"
                                size="small"
                                @click="copyToClipboard(user?.mobile)"
                            ></common-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div :class="rightSideClass">
            <div :class="rightSideTopClass">
                <div v-if="numberOfViews !== null" class="public-profile-header__views">
                    <common-icon name="view" size="medium"></common-icon>
                    <div v-if="!isSmartphoneLarge">{{ $t('common.microcopy.view_number', { n: numberOfViews }) }}</div>
                    <div v-else>{{ numberOfViews }}</div>
                </div>
                <common-favorites-button
                    v-if="showFavorites"
                    :isFavorites="isFavorites"
                    @add-to-favorites-click="emitEvent({ name: 'add-to-favorites-click' })"
                ></common-favorites-button>
                <common-social-share
                    :class="shareClass"
                    :title="shareTitle"
                    :description="shareDescription"
                ></common-social-share>
            </div>
            <common-rating-stars
                v-if="numberOfRatings > 0"
                :value="averageRating"
                :class="ratingStarsClass"
            ></common-rating-stars>
            <div v-if="numberOfRatings > 0" class="public-profile-header__rating-number">
                <u @click="showAllRatings()">{{ $t('common.microcopy.reviews', { n: numberOfRatings }) }}</u>
            </div>
            <all-ratings-modal v-if="showRatingsModal" :ratings="ratings" @close="hideAllRatings"></all-ratings-modal>
        </div>
    </div>
</template>

<script>
    import AllRatingsModal from 'components/modals/rating/AllRatingsModal'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],
        components: { AllRatingsModal },

        props: {
            numberOfRatings: { type: Number, default: 0 },
            numberOfViews: { type: Number, default: null },
            averageRating: { type: Number, default: 0 },
            title: { type: String, default: undefined },
            user: { type: Object, default: undefined },
            company: { type: Object, default: undefined },
            isFavorites: { type: Boolean, default: false },
            isUserProfile: { type: Boolean, default: false },
            isCompanyProfile: { type: Boolean, default: false },
            showFavorites: { type: Boolean, default: true },
            buttonLabel: { type: String, default: undefined },
            shareTitle: { type: String, default: undefined },
            shareDescription: { type: String, default: undefined },
            ratings: { type: Array, default: () => new Array() },
        },

        data: () => ({
            isEmailShow: false,
            isPhoneShow: false,
            showRatingsModal: false,
        }),

        computed: {
            shareClass() {
                return {
                    'public-profile-header__top-bar--right-side-top-share': true,
                    'public-profile-header__top-bar--right-side-top-share-user': this.isUserProfile,
                    'public-profile-header__top-bar--right-side-top-share-company': this.isCompanyProfile,
                    'public-profile-header__top-bar--right-side-top-share-company-rating':
                        this.isCompanyProfile && this.numberOfRatings > 0,
                }
            },
            ratingStarsClass() {
                return {
                    'public-profile-header__rating-stars': true,
                    'public-profile-header__rating-stars-company-rating': this.isCompanyProfile && this.numberOfRatings > 0,
                    'public-profile-header__rating-stars-user-rating': this.isUserProfile && this.numberOfRatings > 0,
                }
            },

            rightSideTopClass() {
                return {
                    'public-profile-header__top-bar--right-side-top': true,
                    'public-profile-header__top-bar--right-side-top--company-rating':
                        this.isCompanyProfile && this.numberOfRatings > 0,
                    'public-profile-header__top-bar--right-side-top--service': !this.isCompanyProfile && !this.isUserProfile,
                }
            },

            rightSideClass() {
                return {
                    'public-profile-header__top-bar--right-side': true,
                    'public-profile-header__top-bar--right-side-service': !this.isCompanyProfile && !this.isUserProfile,
                }
            },
        },

        methods: {
            copyToClipboard(text) {
                navigator.clipboard.writeText(text)
                this.$toast.success(this.$t('common.microcopy.copied'))
            },

            showAllRatings() {
                this.showRatingsModal = true
            },

            hideAllRatings() {
                this.showRatingsModal = false
            },
        },
    }
</script>

<style lang="scss">
    @import 'styles/components/common/_public-profile-header.scss';
</style>
